'use client'

import type { FC } from 'react'
import { Badge } from '@/components/shadcn/badge'

type IconComponent = FC<{ className?: string }>

export type DirectiveTextSegment =
  | { readonly kind: 'text'; readonly text: string }
  | {
      readonly kind: 'mention'
      readonly type: string
      readonly label: string
      readonly id: string
    }

export type DirectiveTextFormatter = {
  /** Parse text into alternating text and directive segments. */
  parse(text: string): readonly DirectiveTextSegment[]
}

export type CreateDirectiveTextOptions = {
  /** Maps a directive `type` to an icon component. */
  iconMap?: Record<string, IconComponent>
  /** Icon rendered when `iconMap` has no entry for the segment type. */
  fallbackIcon?: IconComponent
}

export interface DirectiveChipProps {
  directiveId: string
  directiveType: string
  label: string
  Icon?: IconComponent
  dataSlot?: string
}

export function DirectiveChip({ directiveId, directiveType, label, Icon, dataSlot }: DirectiveChipProps) {
  return (
    <Badge
      {...(dataSlot ? { 'data-slot': dataSlot } : {})}
      aria-label={`${directiveType}: ${label}`}
      className="aui-directive-chip items-baseline px-1.5 py-0.5 text-[13px] leading-none [&_svg]:self-center"
      data-directive-id={directiveId}
      data-directive-type={directiveType}
      variant="secondary"
    >
      {Icon && <Icon />}
      {label}
    </Badge>
  )
}

/** Creates a text component that parses directive syntax and renders inline chips. */
export function createDirectiveText(
  formatter: DirectiveTextFormatter,
  options?: CreateDirectiveTextOptions
): FC<{ text: string }> {
  const iconMap = options?.iconMap
  const fallbackIcon = options?.fallbackIcon

  const Component: FC<{ text: string }> = ({ text }) => {
    const segments = formatter.parse(text)

    if (segments.length === 1 && segments[0]!.kind === 'text') {
      return <>{text}</>
    }

    return (
      <>
        {segments.map((seg, i) => {
          if (seg.kind === 'text') {
            return (
              <span key={i} className="whitespace-pre-wrap">
                {seg.text}
              </span>
            )
          }

          const Icon = iconMap?.[seg.type] ?? fallbackIcon
          return (
            <DirectiveChip
              key={i}
              dataSlot="directive-text-chip"
              directiveId={seg.id}
              directiveType={seg.type}
              Icon={Icon}
              label={seg.label}
            />
          )
        })}
      </>
    )
  }
  Component.displayName = 'DirectiveText'
  return Component
}
